/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosCard, CosmosLink, CosmosTag, CosmosText, EdsIcon } from "@components/elements";
import { ResellerCardFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";
import { useMemo } from "react";

const cardStyles = css({
	minW: "0",
	"&:has(.cosmos-card__link[href]:hover), &:has(.cosmos-card__link[href]:focus-visible)": {
		backgroundColor: "eds.palette.transparent.light.010",
		"& .cosmos-card__link .tag": {
			backgroundColor: "eds.palette.transparent.light.035",
		},
	},
});

const contentStyles = css({
	alignItems: "start",
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	flexGrow: "1",
	minW: "0",
	height: "100%",
	maxW: "100%",
	gap: "eds.dimension.32",
});

const titleStyle = css({
	display: "flex",
	w: "full",
	justifyContent: "space-between",
});

const linkStyles = css({
	lineHeight: "24px",
	textOverflow: "ellipsis",
	overflow: "hidden",
	maxWidth: "100%",
	whiteSpace: "nowrap",
	_hover: { textDecoration: "underline" },
	"& svg": { display: "inline-block", marginInlineEnd: "eds.dimension.10" },
});

const tagStyles = css({
	padding: "eds.dimension.6",
	justifyContent: "center",
	pointerEvents: "none",
	maxW: "eds.dimension.32",
	maxH: "eds.dimension.32",
	"& p": {
		display: "none",
	},
	"& svg": {
		maxW: "eds.dimension.20",
		maxH: "eds.dimension.20",
	},
});

type ResellerSearchCardProps = Pick<ResellerCardFragment, "email" | "website" | "name" | "location" | "phone"> & {
	locationLinkTemplate?: string | null;
	viewLocationLabel?: string | null;
};

export function ResellerSearchCard({
	email,
	website,
	name,
	phone,
	location,
	locationLinkTemplate,
	viewLocationLabel,
}: ResellerSearchCardProps): React.JSX.Element {
	const locationHref = useMemo(
		() =>
			(!!location &&
				locationLinkTemplate
					?.replace("{name}", name || "")
					.replace("{latitude}", location?.latitude?.toString() || "")
					.replace("{longitude}", location?.longitude?.toString() || "")) ||
			"",
		[location, locationLinkTemplate, name],
	);

	const nameContent = name ? (
		<CosmosText variant="headingLG" as="h3" color="eds.text.contrast" className={css({ lineHeight: "32px" })}>
			{name}
		</CosmosText>
	) : null;

	return (
		<CosmosCard className={cx("reseller-card", cardStyles)} variant="material" borderRadius={24}>
			<CosmosCard.Content className={cx("reseller-card-content", contentStyles)}>
				<VStack gap="eds.dimension.20" alignItems="flex-start" justifyContent="flex-start" w="full" h="full">
					{website ? (
						<CosmosCard.Link href={website || ""} className={titleStyle}>
							{nameContent}
							<CosmosTag icon="ChevronRight" className={tagStyles} size="sm" label={name} />
						</CosmosCard.Link>
					) : (
						nameContent
					)}
					<VStack gap="eds.dimension.10" alignItems="start" width="full">
						{phone ? (
							<CosmosLink href={`callto:${phone}`} className={linkStyles}>
								<EdsIcon icon="Phone" />
								{phone}
							</CosmosLink>
						) : null}
						{email && email?.includes("@") ? (
							<CosmosLink href={`mailto:${email}`} className={linkStyles}>
								<EdsIcon icon="Envelope" />
								{email}
							</CosmosLink>
						) : null}
						{!email?.includes("@") && website ? (
							<CosmosLink href={email || website || ""} className={linkStyles}>
								<EdsIcon icon="Globe" />
								{email || website}
							</CosmosLink>
						) : null}
					</VStack>
					{locationHref ? (
						<CosmosTag
							icon="ArrowUpRightFromSquare"
							href={locationHref}
							label={viewLocationLabel || "View location"}
							className={css({ marginBlockStart: "auto" })}
						/>
					) : null}
				</VStack>
			</CosmosCard.Content>
		</CosmosCard>
	);
}
